import { t } from 'i18next';
import * as yup from 'yup';

import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { CompanyBasic } from '~hooks/useCompany/models';
import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import UserStore from '~store/UserStore';
import { emptyStringToNull } from '~utils/yupTransforms';

export const taxRateScheduleSchema = yup.object().shape({
  company: yup
    .object()
    .shape({
      label: yup.string().trim().required(),
      value: yup.string().trim().required(),
    })
    .required(t('form_validation_errors.required_simple')),
  name: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
  rate: yup
    .number()
    .nullable()
    .min(0, t('form_validation_errors.minimum_is_num', { num: 0 }))
    .max(100, t('form_validation_errors.maximum_is_num', { num: 100 }))
    .test(
      'max-decimals',
      t('form_validation_errors.max_decimal_places', { count: 4 }),
      (value) => {
        if (value === null || value === undefined) return true;
        const parts = String(value).split('.');
        return !parts[1] || parts[1].length <= 4;
      },
    )
    .transform(emptyStringToNull),
  externalAccountingId: yup.string().trim().nullable(),
  externalId: yup.string().trim().nullable(),
});

export type TaxRateScheduleDto = yup.InferType<typeof taxRateScheduleSchema>;
export type TaxRateScheduleDtoAsyncError = {
  field: keyof TaxRateScheduleDto;
  message: string;
};

export const setTaxRateScheduleSchemaDefaultValues = (
  userStore: UserStore,
  taxRateSchedule?: TaxRateSchedule,
) => {
  const companyFromStore = userStore.currentCompanies[0] || userStore.user.company;
  const company = taxRateSchedule?.company
    ? {
        label: taxRateSchedule.company.legalName,
        value: taxRateSchedule.company.id,
      }
    : companyFromStore
      ? { label: companyFromStore.legalName, value: companyFromStore.id }
      : null;

  const defaultValues: TaxRateScheduleDto = {
    company: company as AutocompleteFieldOption<CompanyBasic>,
    name: taxRateSchedule?.name ?? '',
    rate:
      taxRateSchedule?.rate === null || taxRateSchedule?.rate === undefined
        ? null
        : Number(taxRateSchedule.rate),
    externalAccountingId: taxRateSchedule?.externalAccountingId ?? '',
    externalId: taxRateSchedule?.externalId ?? '',
  };

  return defaultValues;
};
